import './player.css';

import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { filter, map, throttleTime } from 'rxjs/operators';

import {
  KalturaPlayer,
  PlaybackStatuses,
  usePlayer,
  usePlayerUpdates,
} from '@/infrastructure/libs/kaltura-react';
import { ClassroomViewModel } from '@/presentation/screens/classroom/classroom.vm';

import { StudyPlanOverlay } from '../study-plan-overlay';
import playerTranslations from './translations';

export interface VideoProgress {
  currentTime: number;
  duration: number;
  usage: number;
}

export interface EntryConfig {
  entryId: string;
  alternateEntryId?: string;
}

export interface PlayerProps {
  entryConfig: EntryConfig;
  onVideoProgress: { (videoProgress: VideoProgress): void };
  onVideoPlaying?: () => void;
  autoPlay?: boolean;
}

interface HandlePlayerLoadedProps {
  playerId: string;
}

const ELAPSED_VIDEO_TIME_MS = 10000;

const Player: React.FC<PlayerProps> = (props: PlayerProps) => {
  const vm = useViewModel(ClassroomViewModel);

  const { guid } = useParams();

  const nextVideo = vm.nextVideoForLesson;

  const { entryConfig, onVideoPlaying, onVideoProgress, autoPlay } = props;

  const [entryId, setEntryId] = useState(entryConfig.entryId);
  const [playerId, setPlayerId] = useState('');
  const { getVideoProgress, getPlayerInstance } = usePlayer(playerId);
  const { playerState$, playerTime$ } = usePlayerUpdates(playerId);

  const [position, setPosition] = useState<number>(0);
  const playerInstance = getPlayerInstance();

  const [autoPlayVideo, setAutoPlayVideo] = useState(autoPlay);

  useEffect(() => {
    setAutoPlayVideo(autoPlay);
  }, [autoPlay]);

  useEffect(() => {
    setEntryId(entryConfig.entryId);
  }, [entryConfig]);

  useEffect(() => {
    if (!playerId) {
      return;
    }

    const stateSubscription = playerState$.subscribe((state) => {
      if (state === PlaybackStatuses.Playing) {
        if (onVideoPlaying) {
          onVideoPlaying();
        }
      }
    });

    return () => {
      stateSubscription.unsubscribe();
    };
  }, [playerState$]);

  useEffect(() => {
    if (!playerId) {
      return;
    }

    const timeSubscription = playerTime$
      .pipe(
        throttleTime(5000),
        map(() => getVideoProgress()),
        filter(
          (videoProgress) =>
            !!videoProgress && !!videoProgress.currentTime && videoProgress.currentTime != 0,
        ),
      )
      .subscribe((videoProgress) => {
        if (videoProgress) onVideoProgress(videoProgress);
      });

    const renderBanner = playerTime$.subscribe((videoProgress) => {
      if (videoProgress > ELAPSED_VIDEO_TIME_MS) {
        renderBanner.unsubscribe();
      }
    });

    const handleNextVideo = playerTime$.subscribe(setPosition);

    return () => {
      timeSubscription.unsubscribe();
      renderBanner.unsubscribe();
      handleNextVideo.unsubscribe();
    };
  }, [playerId, playerState$, playerTime$]);

  const onPlayerProgress = (): void => {
    if (shouldGoNextVideo() && autoPlayVideo) {
      if (nextVideo !== undefined) {
        vm.setCurrentVideo(nextVideo);
        vm.toggleMobileSidebar();
        vm.updateCurrentLesson(guid);
      }
    }
  };

  useEffect(onPlayerProgress, [position]);

  if (!vm.classroom) {
    return null;
  }

  const handlePlayerLoaded = (data: HandlePlayerLoadedProps) => {
    const { playerId } = data;

    if (!playerId) {
      return;
    }
    setPlayerId(playerId);
  };

  const customizeConfig = (config: Record<string, any>): Record<string, unknown> => {
    const newConfig = {
      ...config,
      ui: {
        ...(config.ui || {}),
        locale: 'pt_br',
        translations: playerTranslations,
      },
    };
    return newConfig;
  };

  const shouldGoNextVideo = (): boolean => {
    const currentTime = playerInstance?.currentTime;
    const currentDuration = playerInstance?.duration;
    const currentPercentage = Math.round((currentTime / currentDuration) * 100);
    return currentPercentage === 100;
  };

  return (
    <div className="player">
      {vm.studyPlan && vm.showCompletedStudyPlanOverlay ? (
        <StudyPlanOverlay />
      ) : (
        <KalturaPlayer
          entryId={entryId}
          autoplay={autoPlay}
          onPlayerLoaded={handlePlayerLoaded}
          customizeConfig={customizeConfig}
          startTime={vm.startTime}
        />
      )}
    </div>
  );
};

export default observer(Player);
